import * as React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import { SEO } from "@/components/SEO";
import { BreadcrumbNav } from "@/components/ui/breadcrumb-nav";
import PDFDownloadButton from "@/components/ui/pdf-download-button";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { RelatedProducts } from "@/components/RelatedProducts";
import { useScrollToTop } from "@/hooks/useScrollToTop";
import { Footer } from "@/components/Footer";
import { ShippingTimeInfo } from "@/components/ShippingTimeInfo";
import { 
  Crown, Shield, Award, Sparkles, Droplets, 
  Thermometer, Zap, Heart, ArrowRight, Star
} from "lucide-react";

// Import AURELUX series images
import aureluxSeriesProductMain from "@/assets/aurelux-series-product-main.jpg";
import aureluxSeriesLifestyle from "@/assets/aurelux-series-lifestyle.jpg";
import aureluxSeriesProductAngle from "@/assets/aurelux-series-product-angle.jpg";
import aureluxSeriesControls from "@/assets/aurelux-series-controls.jpg";
import aureumLogo from "@/assets/aureum-logo-new.png";

import { PriceFrom } from "@/components/products/PriceFrom";
const Aurelux = () => {
  useScrollToTop();

  const specifications = [
    { label: "Dimensions", value: "710mm × 410mm × 820mm" },
    { label: "Weight", value: "85kg" },
    { label: "Power Consumption", value: "1800W" },
    { label: "Water Usage", value: "3.5L/2.0L Dual Flush" },
    { label: "Warranty", value: "10 Years Platinum Coverage" },
    { label: "Installation", value: "White Glove Service Included" },
    { label: "Material Finish", value: "24K Gold Accents Available" },
    { label: "Certification", value: "CE Certified, ISO 9001 Premium" }
  ];

  const features = [
    {
      icon: <Crown className="w-8 h-8" />,
      title: "24K Gold Accents",
      description: "Handcrafted luxury details with genuine 24-karat gold accents for the ultimate in opulent bathroom design"
    },
    {
      icon: <Star className="w-8 h-8" />,
      title: "Bespoke Experience",
      description: "Fully customizable user profiles with AI-powered preference learning for personalized luxury comfort"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Platinum Service",
      description: "10-year platinum warranty with dedicated concierge-level customer service and priority support"
    },
    {
      icon: <Sparkles className="w-8 h-8" />,
      title: "Exclusive Technology",
      description: "Proprietary smart features including advanced UV sterilization and premium air purification systems"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="AURELUX Gold Standard Smart Toilet | Ultra-Luxury"
        description="AURELUX ultra-luxury smart toilet with gold-accent finishing, UV sterilization, air purification and white-glove installation for prestige residences."
        keywords="ultra-luxury smart toilet, AURELUX gold standard, 24k gold bathroom fixtures, bespoke smart toilet, platinum service bidet, exclusive bathroom technology, handcrafted luxury toilet, concierge smart sanitary system, high-end European bathroom, ultra-premium toilet, white glove installation, luxury estate bathroom, UV sterilization toilet, voice control toilet, heated toilet seat control, bidet functionality"
        url="/products/aurelux"
        image="https://www.aureum7trading.com/images/products/aurelux-series-product-main.jpg"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Smart Toilets", url: "/smart-toilets" },
          { name: "AURELUX", url: "/products/aurelux" }
        ]}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Product",
          "name": "AURELUX Smart Toilet",
          "description": "The gold standard in ultra-luxury smart toilets with 24K gold accents, bespoke AI experience, and concierge-level platinum service",
          "brand": {
            "@type": "Brand",
            "name": "Aureum VII Trading"
          },
          "image": [
            "https://www.aureum7trading.com/images/products/aurelux-series-product-main.jpg"
          ],
          "gtin13": "0795479810012",
          "manufacturer": {
            "@type": "Organization",
            "name": "Aureum VII Trading"
          },
          "sku": "AURELUX-2024",
          "mpn": "AURELUX",
          "gtin": "AVT-AUR-012",
          "offers": {
            "@type": "Offer",
            "availability": "https://schema.org/InStock",
            "priceCurrency": "EUR",
            "price": 600,
            "priceValidUntil": "2026-12-31",
            "itemCondition": "https://schema.org/NewCondition",
            "url": "https://www.aureum7trading.com/products/aurelux",
            "seller": {
              "@type": "Organization",
              "name": "Aureum VII Trading",
              "url": "https://www.aureum7trading.com"
            },
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "EUR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"]
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 1,
                  "maxValue": 3,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 10,
                  "maxValue": 40,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "@id": "https://www.aureum7trading.com/return-policy",
              "applicableCountry": ["ES", "DE", "FR", "IT", "GB", "PT", "NL", "BE", "AT"],
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 14,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn",
              "itemCondition": "https://schema.org/NewCondition",
              "refundType": "https://schema.org/FullRefund"
            }
          },
          "additionalProperty": [
            {
              "@type": "PropertyValue",
              "name": "Gold Accents",
              "value": "24K Gold Available"
            },
            {
              "@type": "PropertyValue",
              "name": "Warranty",
              "value": "10 Years Platinum"
            },
            {
              "@type": "PropertyValue",
              "name": "Installation",
              "value": "White Glove Service"
            }
          ]
        }}
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-marble">
        <div className="container mx-auto max-w-7xl">
          <BreadcrumbNav items={[
            { label: "Home", href: "/" },
            { label: "Smart Toilets", href: "/smart-toilets" },
            { label: "AURELUX Series" }
          ]} />
          
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center mt-8">
            <div className="fade-in-observer">
              <div className="mb-6">
                <Badge className="bg-amber-500/10 text-amber-600 border-amber-500/20 mb-4">
                  Flagship Ultra-Luxury Collection
                </Badge>
                <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 text-primary leading-tight">
                  AURELUX
                  <span className="block text-2xl md:text-3xl text-amber-600 font-medium mt-2">
                    The Gold Standard
                  </span>
                </h1>
                <p className="font-inter text-lg text-muted-foreground leading-relaxed mb-8">
                  The AURELUX represents the absolute pinnacle of <Link to="/smart-toilets" className="text-primary hover:underline font-semibold">luxury smart toilet</Link> excellence—where handcrafted artistry meets cutting-edge European technology. This ultra-premium flagship <Link to="/blog/luxury-smart-toilet-guide" className="text-primary hover:underline">intelligent sanitary system</Link> features authentic 24-karat gold accents, bespoke AI-powered user experience with advanced preference learning, exclusive technology suite including proprietary UV sterilization and premium air purification, and concierge-level platinum service with 10-year comprehensive warranty. Perfect for ultra-luxury estates, high-end residential projects, and discerning clients seeking the gold standard in <Link to="/blog/smart-toilet-vs-bidet" className="text-primary hover:underline font-semibold">smart bathroom technology</Link>. Includes white glove installation and dedicated support. Compare with our <Link to="/products/aria" className="text-primary hover:underline">architectural ARIA H60</Link> or explore our <Link to="/products/luxa" className="text-primary hover:underline">flagship LUXA M80</Link>.
                </p>
              </div>
              
              <PriceFrom price={600} />

              {/* Features List */}
              <div className="mb-8 space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Handcrafted 24K gold accents</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Bespoke AI user experience</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="font-inter text-muted-foreground">Platinum concierge service</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                  <span className="font-inter text-muted-foreground">White glove installation</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <PDFDownloadButton productName="AURELUX" />
                <Button variant="outline" asChild>
                  <Link to="/smart-toilets">← Back to Collections</Link>
                </Button>
              </div>
              
              <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-amber-600">24K</div>
                  <div className="font-inter text-sm text-muted-foreground">Gold Accents</div>
                </div>
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-amber-600">10 Yrs</div>
                  <div className="font-inter text-sm text-muted-foreground">Platinum</div>
                </div>
                <div className="text-center">
                  <div className="font-playfair text-2xl font-bold text-amber-600">VIP</div>
                  <div className="font-inter text-sm text-muted-foreground">Concierge</div>
                </div>
              </div>
            </div>
            
            <div className="fade-in-observer">
              <ImageLightbox
                src={aureluxSeriesProductMain}
                alt="AURELUX ultra-luxury smart toilet featuring 24K gold accents, bespoke AI technology, and handcrafted premium design"
                className="w-full h-96 sm:h-[500px] lg:h-[600px] object-cover rounded-lg shadow-elegant cursor-pointer hover:shadow-gold transition-all duration-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              The Gold Standard Excellence
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              AURELUX represents the absolute pinnacle of luxury, combining handcrafted artistry 
              with exclusive technology reserved for the most discerning clientele.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <article key={index} className="fade-in-observer rounded-lg border bg-card shadow-elegant hover:shadow-gold transition-all duration-500 hover:-translate-y-2 p-8 text-center space-y-4">
                <div className="w-16 h-16 mx-auto bg-amber-500/10 rounded-full flex items-center justify-center text-amber-600">
                  {feature.icon}
                </div>
                <h3 className="font-playfair text-xl font-bold text-primary">{feature.title}</h3>
                <p className="font-inter text-muted-foreground leading-relaxed">{feature.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Product Gallery */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-marble">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Handcrafted Luxury
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              Every AURELUX is a masterpiece of artisanal craftsmanship and cutting-edge technology.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="fade-in-observer">
              <ImageLightbox
                src={aureluxSeriesProductAngle}
                alt="AURELUX smart toilet angle view showcasing premium materials and 24K gold accent details"
                className="w-full h-80 object-cover rounded-lg shadow-elegant hover:shadow-gold transition-all duration-500 mb-4"
              />
              <h3 className="font-playfair text-xl font-bold text-primary mb-2">Premium Materials</h3>
              <p className="font-inter text-muted-foreground">24K gold accents and luxury finishes</p>
            </div>

            <div className="fade-in-observer">
              <ImageLightbox
                src={aureluxSeriesControls}
                alt="AURELUX smart toilet control panel with intuitive touch interface and premium design"
                className="w-full h-80 object-cover rounded-lg shadow-elegant hover:shadow-gold transition-all duration-500 mb-4"
              />
              <h3 className="font-playfair text-xl font-bold text-primary mb-2">Intuitive Controls</h3>
              <p className="font-inter text-muted-foreground">Premium touch interface with AI learning</p>
            </div>

            <div className="fade-in-observer">
              <ImageLightbox
                src={aureluxSeriesLifestyle}
                alt="AURELUX ultra-luxury smart toilet installed in opulent bathroom setting with marble and gold accents"
                className="w-full h-80 object-cover rounded-lg shadow-elegant hover:shadow-gold transition-all duration-500 mb-4"
              />
              <h3 className="font-playfair text-xl font-bold text-primary mb-2">Luxury Integration</h3>
              <p className="font-inter text-muted-foreground">Perfect for ultra-luxury estates</p>
            </div>
          </div>
        </div>
      </section>

      {/* Technical Specifications */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12 sm:mb-16 fade-in-observer">
            <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold mb-6 text-primary">
              Uncompromising Excellence
            </h2>
            <p className="font-inter text-lg text-muted-foreground max-w-3xl mx-auto">
              Engineered to exceed the highest standards of luxury and performance.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
            <div className="fade-in-observer rounded-lg border bg-card shadow-elegant p-8 space-y-4">
              <h3 className="font-playfair text-2xl font-bold mb-6 text-primary">AURELUX Specifications</h3>
              {specifications.map((spec, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-border last:border-b-0">
                  <span className="font-inter font-medium text-muted-foreground">{spec.label}</span>
                  <span className="font-inter text-primary font-semibold text-right max-w-[60%]">{spec.value}</span>
                </div>
              ))}
            </div>

            <div className="space-y-6 fade-in-observer">
              <div className="rounded-lg border bg-card shadow-elegant p-6">
                <h4 className="font-playfair text-lg font-semibold text-primary mb-4">Exclusive Features</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Crown className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">24K gold accents handcrafted by artisans</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Bespoke AI with preference learning</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Shield className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Advanced UV sterilization system</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border bg-card shadow-elegant p-6">
                <h4 className="font-playfair text-lg font-semibold text-primary mb-4">Premium Technology</h4>
                <ul className="space-y-3 text-muted-foreground">
                  <li className="flex items-start gap-3">
                    <Droplets className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Multi-pattern bidet with massage functions</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Thermometer className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Instant heating with precision control</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <Zap className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                    <span className="font-inter text-sm">Premium air purification technology</span>
                  </li>
                </ul>
              </div>

              <div className="rounded-lg border bg-card shadow-elegant p-6 flex items-center gap-4">
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h4 className="font-playfair text-lg font-semibold text-primary">Platinum Service</h4>
                  <p className="font-inter text-sm text-muted-foreground">10-Year Warranty + Concierge Support</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping & Delivery Information */}
      <section className="py-16 px-4 sm:px-6 lg:px-12">
        <div className="container mx-auto max-w-7xl fade-in-observer">
          <ShippingTimeInfo variant="detailed" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-12 bg-gradient-to-r from-amber-500/10 via-amber-600/10 to-accent/10">
        <div className="container mx-auto max-w-4xl text-center fade-in-observer">
          <h2 className="font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-6 text-primary">
            Experience The Gold Standard
          </h2>
          <p className="font-inter text-lg mb-8 max-w-2xl mx-auto text-muted-foreground">
            Transform your luxury estate with AURELUX—the pinnacle of European smart toilet excellence. 
            Contact our platinum service team for bespoke consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-amber-600 to-amber-500 text-white hover:from-amber-700 hover:to-amber-600 font-inter font-semibold px-8 py-3 shadow-lg">
                Request VIP Consultation
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/smart-toilets">
              <Button variant="outline" className="border-amber-600 text-amber-600 hover:bg-amber-50 font-inter font-semibold px-8 py-3">
                View All Collections
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Related Products */}
      <RelatedProducts currentProduct="AURELUX" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Aurelux;